import type { FuncKey } from './keys';

export const en: Record<FuncKey, string> = {
  'app.eyebrow': 'Math · Functions',
  'app.title': 'Functions',
  'panel.params': 'Parameters',
  'panel.experiment': 'Experiment',
  'hint.line1': 'Drag sliders, edit expressions, watch the graph update.',
  'hint.line2':
    'Each experiment focuses on one operation: families, transformations, composition, inverses, properties, piecewise, multi-representations.',
  'experiment.family-zoo.label': 'Family zoo',
  'experiment.transformations.label': 'Transformations',
  'experiment.composition.label': 'Composition',
  'experiment.inverses.label': 'Inverses',
  'experiment.domain-range-lab.label': 'Domain & range lab',
  'experiment.behavior-and-features.label': 'Behavior & features',
  'experiment.piecewise-builder.label': 'Piecewise builder',
  'experiment.representations.label': 'Representations',
  'experiment.family-zoo.info':
    'Pick a function family, slide its parameters, see the graph reflow.',
  'experiment.transformations.info':
    'Apply a·f(b·(x − h)) + k to a chosen base; watch each parameter change the shape.',
  'experiment.composition.info':
    'Compose two functions; explore the domain restrictions that arise.',
  'experiment.inverses.info':
    'Reflect a function across y = x. Restricting domain may be required for invertibility.',
  'experiment.domain-range-lab.info':
    'Drag interval handles to restrict the domain; watch the range respond.',
  'experiment.behavior-and-features.info': 'Find zeros, extrema, intercepts, and asymptotes.',
  'experiment.piecewise-builder.info':
    'Build piecewise functions; highlight continuity vs jump discontinuities.',
  'experiment.representations.info':
    'See the same function as formula, table, graph, and verbal description.',
  'experiment.transformations.info.trig-cross-link':
    'See the trigonometry demo for the unit-circle treatment ↗',
  'experiment.composition.info.trig-cross-link':
    'See the trigonometry demo for the unit-circle treatment ↗',
  'experiment.inverses.info.trig-cross-link':
    'See the trigonometry demo for the unit-circle treatment ↗',
  'btn.reset': 'Reset',
  'btn.reset-view': 'Reset view',
  'btn.randomise': 'Randomise',
  'btn.reveal': 'Reveal',
  'btn.add-piece': 'Add piece',
  'btn.remove-piece': 'Remove piece',
  'btn.clear-all': 'Clear all',
  'btn.swap': 'Swap',
  'btn.animate': 'Animate',
  'btn.auto-find': 'Auto-find features',
  'family.linear.label': 'Linear',
  'family.quadratic.label': 'Quadratic',
  'family.cubic.label': 'Cubic',
  'family.polynomial.label': 'Polynomial',
  'family.rational.label': 'Rational',
  'family.exponential.label': 'Exponential',
  'family.logarithmic.label': 'Logarithmic',
  'family.absolute-value.label': 'Absolute value',
  'family.square-root.label': 'Square root',
  'family.sin.label': 'Sine',
  'family.cos.label': 'Cosine',
  'family.tan.label': 'Tangent',
  'param.family.label': 'Family',
  'param.parent.label': 'Show parent function',
  'param.predict-mode.label': 'Predict mode',
  'feature.vertex': 'Vertex',
  'feature.zeros': 'Zeros',
  'message.predict-correct': 'Correct!',
  'message.predict-wrong': 'Try again',
  'param.a.label': 'a (vertical scale)',
  'param.b.label': 'b (horizontal scale)',
  'param.h.label': 'h (horizontal shift)',
  'param.k.label': 'k (vertical shift)',
  'param.base.label': 'Base function',
  'param.show-stepwise.label': 'Show stepwise',
  'param.f.label': 'f(x)',
  'param.g.label': 'g(x)',
  'param.order.label': 'Order',
  'param.show-intermediate.label': 'Show intermediate g(x₀)',
  'param.show-domain-restriction.label': 'Show domain restriction',
  'param.restrict.label': 'Restrict domain',
  'param.restrict.not-needed': 'Already one-to-one — no restriction needed',
  'param.restrict.use-branch': 'Use one-to-one branch',
  'param.restrict.unavailable': 'No automatic branch suggestion is available for this expression',
  'param.restrict.custom': 'Define custom one-to-one branch',
  'param.show-yx.label': 'Show y = x',
  'message.not-invertible-without-restriction': 'Choose a one-to-one branch to show the inverse',
  'message.restriction-not-one-to-one': 'f(x) must be one-to-one on the selected domain',
  'message.numerical-inverse': 'numerical inverse of f(x)',
  'message.inverse-domain-unresolved': 'inverse domain not resolved',
  'param.domain-start.label': 'Domain start',
  'param.domain-end.label': 'Domain end',
  'param.output-range-start.label': 'Range start',
  'param.output-range-end.label': 'Range end',
  'param.show-excluded.label': 'Show excluded points',
  'btn.auto-domain': 'Auto-detect natural domain',
  'readout.selected-domain': 'Selected domain',
  'readout.natural-domain': 'Natural domain',
  'readout.image-of-domain': 'Sampled y-span over selected domain',
  'readout.no-finite-outputs': 'No finite outputs',
  'feature.zero': 'Zero',
  'feature.extrema': 'Extrema',
  'feature.extremum-min': 'Min',
  'feature.extremum-max': 'Max',
  'feature.y-intercept': 'y-intercept',
  'feature.vertical-asymptote': 'VA',
  'feature.horizontal-asymptote': 'HA',
  'param.show-derivative.label': 'Show formal derivative',
  'param.pieces.label': 'Example',
  'param.piece-expression.label': 'Formula',
  'param.piece-start.label': 'Start',
  'param.piece-end.label': 'End',
  'param.endpoint-included.label': 'Included',
  'param.endpoint-excluded.label': 'Excluded',
  'piece.label': 'Piece',
  'param.highlight-discontinuities.label': 'Highlight discontinuities',
  'param.continuity-check.label': 'Continuity check',
  'preset.step.label': 'Step — −1 / 0 / 1 across three intervals',
  'preset.heaviside.label': 'Heaviside — 0 for x < 0; 1 for x ≥ 0',
  'preset.tent.label': 'Tent — x + 1 then 1 − x on [−1, 1]',
  'preset.relu.label': 'ReLU — 0 for x ≤ 0; x for x > 0',
  'preset.custom.label': 'Custom pieces',
  'param.range-start.label': 'Start',
  'param.range-end.label': 'End',
  'param.range-step.label': 'Step',
  'param.precision.label': 'Precision',
  'param.show-verbal.label': 'Show verbal description',
  'panel.formula': 'Formula',
  'panel.table': 'Table',
  'panel.graph': 'Graph',
  'panel.verbal': 'Verbal description',
};
